import { SocketClient } from '@goldcast/client';
import { throttle } from 'lodash';

import loggerService from '../logger.service';
import trackerService from '../tracker.service';
import { THROTTLE_TIMEOUT, MISSED_PING_THRESHOLD_TIME, ONE_MINUTE } from './constants';

import { getCurrentRoute } from '@/utils/routerAccess';
import { getStore } from '@/utils/storeAccess';

let lastGatewaymsgTimeStamp = 0;
let pingTimeout: NodeJS.Timeout | null = null;

function getSafeSocketRouteContext(): { routeName?: string; eventId?: string } {
  try {
    const route = getCurrentRoute();
    return {
      routeName: route.name as string | undefined,
      eventId: route.params?.eventID as string | undefined
    };
  } catch {
    return {};
  }
}

/** Best-effort diagnostic — wrapped so a logging failure never changes socket control flow. */
function safeSocketLog(event: string, metadata: Record<string, unknown>): void {
  try {
    loggerService.info(event, metadata);
  } catch {
    // best-effort diagnostic — never change socket control flow
  }
}
export class GatewayClient extends SocketClient {
  token = null;

  constructor(url: string, logger: typeof loggerService) {
    super(url, logger, 'gatewaysocket');
  }

  updateMissedPingTimer = throttle(() => {
    this.setMissedPingTimer();
  }, THROTTLE_TIMEOUT);

  setMissedPingTimer() {
    this.clearMissedPingTimer();
    pingTimeout = setTimeout(() => {
      this.checkForInActivityAndReconnectIfNeeded();
    }, MISSED_PING_THRESHOLD_TIME);
  }

  clearMissedPingTimer() {
    if (pingTimeout) {
      clearTimeout(pingTimeout);
    }
  }

  checkForInActivityAndReconnectIfNeeded() {
    const isLastGatewaymsgOlderThanOneMinute = Date.now() - lastGatewaymsgTimeStamp > ONE_MINUTE;
    if (isLastGatewaymsgOlderThanOneMinute) {
      const store = getStore();
      const mmSocketStatus = store?.state.mattermost.socketstatus;

      if (mmSocketStatus === 'online') {
        loggerService.info(
          '[gatewaysocket] No messages in last minute but MM connection is healthy, skipping reconnect'
        );
        this.setMissedPingTimer();
        return;
      }

      loggerService.error('[gatewaysocket] No messages in last minute and MM is not healthy, reconnecting');
      this.clearMissedPingTimer();
      store?.dispatch('apigateway/disconnect').then(() => {
        store?.dispatch('apigateway/subscribeToEvent', {
          eventId: getCurrentRoute().params.eventID
        });
      });
      return;
    }
    this.updateMissedPingTimer();
  }

  preConnect() {
    this.logger.info('Running socket preConnect for gatewaysocket');
    trackerService.track('apigateway', 'shouldBeUsed', { isEnabled: true });

    return Promise.resolve();
  }

  onError(reconnectionAttempt: number) {
    loggerService.error('[gatewaysocket] Connection error', { reconnectionAttempt });
    safeSocketLog('socket_failure', {
      transport: 'gatewaysocket',
      failureCategory: 'gateway_socket_error',
      action: 'reconnect',
      reconnectionAttempt,
      socketStatus: 'error',
      ...getSafeSocketRouteContext()
    });
    if (reconnectionAttempt === 1) {
      trackerService.track('apigateway', 'connectionInitialized', { timestamp: Date.now() });
    }
    this.clearMissedPingTimer();
    trackerService.track('apigateway', 'error');
    getStore()?.commit('apigateway/setSocketStatus', 'error');
  }

  onMessage(event: any) {
    try {
      lastGatewaymsgTimeStamp = Date.now();
      this.updateMissedPingTimer();
      const message = JSON.parse(event.data);
      getStore()?.dispatch('apigateway/processEvent', message);
    } catch (e) {
      loggerService.error('[gatewaysocket] Error parsing event from apigateway', e);
    }
  }

  onClosed() {
    trackerService.track('apigateway', 'disconnections');
    safeSocketLog('socket_failure', {
      transport: 'gatewaysocket',
      failureCategory: 'gateway_socket_closed',
      action: 'closed',
      socketStatus: 'offline',
      ...getSafeSocketRouteContext()
    });
    this.clearMissedPingTimer();
    getStore()?.commit('apigateway/setSocketStatus', 'offline');
  }

  onOpened() {
    this.setMissedPingTimer();
    trackerService.track('apigateway', 'connectionEstablished', { timestamp: Date.now() });
    safeSocketLog('socket_connected', {
      transport: 'gatewaysocket',
      action: 'connected',
      socketStatus: 'online',
      ...getSafeSocketRouteContext()
    });
    const store = getStore();
    store?.commit('apigateway/setBailed', false);
    store?.commit('apigateway/setSocketStatus', 'online');
    const onDemandMode = store?.state.event.event?.on_demand_mode;
    // not calling processSocketOpenDone here as mm is our primary socket
    // and sse is fallback to it. so this will unnecessarily trigger extra uber
    // call for every user who can connect to both the sockets successfully.
    // performing remaining actions here only.

    const currentRoute = getCurrentRoute();
    // subscribe to event
    if (!onDemandMode && currentRoute.params.eventID) {
      store?.dispatch('apigateway/subscribeToEvent', {
        eventId: currentRoute.params.eventID
      });
    }
    // subscribe to broadcast
    if (currentRoute.name === 'Broadcast' && !onDemandMode) {
      store?.dispatch('apigateway/subscribeToBroadcast', {
        broadcastId: currentRoute.params.id
      });
    }
    this.sendMessage({
      action: 'ping',
      data: {}
    });
    return store?.commit('apigateway/setFirstConnect', true);
  }

  onBail() {
    trackerService.track('apigateway', 'disconnections');
    loggerService.error('[gatewaysocket] Bailing on gateway connection');
    safeSocketLog('socket_failure', {
      transport: 'gatewaysocket',
      failureCategory: 'gateway_socket_bailed',
      action: 'bail',
      socketStatus: 'offline',
      ...getSafeSocketRouteContext()
    });
    const store = getStore();
    store?.commit('apigateway/setBailed', true);
    return store?.dispatch('apigateway/setSocketStatus', 'offline');
  }
}
